import { Component, OnInit } from "@angular/core";
import { ModalName } from "src/app/enums/modal-name";
import { NotificationStatus } from "src/app/enums/notification-status";
import { IContact } from "src/app/models/contact/contact";
import { INotification } from "src/app/models/notification/notification";
import { IUpdateNotificationPayload } from "src/app/models/notification/update-notification-payload";
import { ModalService } from "src/app/services/modal.service";
import { NotificationService } from "src/app/services/notification.service";

interface IEditableNotification extends INotification {
  isNew: boolean;
  originalSendTo: string;
}

@Component({
  selector: "app-notifications",
  templateUrl: "./notifications.component.html",
  styleUrls: ["./notifications.component.scss"],
})
export class NotificationsComponent implements OnInit {
  sourceNotifications: INotification[] = [];
  notifications: IEditableNotification[] = [];
  deletedNotifications: IEditableNotification[] = [];
  notificationsPageNumber = "1";
  currentNotificationIndex = 0;
  isLoading = true;

  readonly skeletonRows = [1, 2, 3];

  readonly notificationSendToPattern =
    '^(([^<>()[]\\.,;:s@"]+(.[^<>()[]\\.,;:s@"]+)*)|.(".+"))@(([[0-9]{1,3}.[0-9]{1,3}.[0-9]{1,3}.[0-9]{1,3}])|(([a-zA-Z-0-9]+.)+[a-zA-Z]{2,}))$';
  readonly notificationAttachmentTypes = ["attachment", "none"];
  readonly notificationsLimit = 5;

  private readonly _pageSize = "10";

  constructor(
    private readonly _notificationService: NotificationService,
    private readonly _modalService: ModalService
  ) {}

  get notificationStatuses() {
    return Object.values(NotificationStatus);
  }

  get isSomeNotificationSendToInvalid() {
    return this.notifications.some(
      n => n.sendTo.toLowerCase().match(this.notificationSendToPattern)?.length === 0
    );
  }

  deleteNotification(notification: IEditableNotification) {
    this.notifications = this.notifications.filter(n => n !== notification);

    if (!notification.isNew) {
      this.deletedNotifications.push(notification);
    }
  }

  resetNotificationSendTo(notificationIndex: number) {
    this.notifications[notificationIndex].sendTo = "";
  }

  openSearchContactModal(currentNotificationIndex: number) {
    this.currentNotificationIndex = currentNotificationIndex;
    this._modalService.openModal(ModalName.SearchContact);
  }

  selectContact(contact: IContact) {
    this.notifications[this.currentNotificationIndex].sendTo = contact.email;
  }

  changeNotificationOnSendStatus(notificationIndex: number, status: NotificationStatus) {
    this.notifications[notificationIndex].onSendStatus = status;
  }

  changeNotificationOnReceiveStatus(notificationIndex: number, status: NotificationStatus) {
    this.notifications[notificationIndex].onReceiveStatus = status;
  }

  getNotificationAttachmentOnSendType(notification: INotification) {
    return notification.hasAttachmentOnSend ? "attachment" : "none";
  }

  getNotificationAttachmentOnReceiveType(notification: INotification) {
    return notification.hasAttachmentOnReceive ? "attachment" : "none";
  }

  changeNotificationHasAttachmentOnSend(notificationIndex: number, status: string) {
    this.notifications[notificationIndex].hasAttachmentOnSend = status === "attachment";
  }

  changeNotificationHasAttachmentOnReceive(notificationIndex: number, status: string) {
    this.notifications[notificationIndex].hasAttachmentOnReceive = status === "attachment";
  }

  createNotification() {
    this.notifications.push({
      sendTo: "",
      hasAttachmentOnSend: false,
      hasAttachmentOnReceive: false,
      onSendStatus: NotificationStatus.None,
      onReceiveStatus: NotificationStatus.None,
      isNew: true,
      originalSendTo: "",
    });
  }

  resetNotifications() {
    this.notifications = this.sourceNotifications.map(n => ({ ...n, isNew: false, originalSendTo: n.sendTo }));
    this.deletedNotifications = [];
  }

  async saveNotifications() {
    await Promise.all([
      ...this.getCreateNotificationPromises(),
      ...this.getUpdateNotificationPromises(),
      ...this.getDeleteNotificationPromises(),
    ])
      .then(() => this.getNotifications())
      .catch(console.error);
  }

  private getCreateNotificationPromises() {
    return this.notifications
      .filter(n => n.isNew)
      .map(n => this._notificationService.create(this.toNotificationPayload(n)).toPromise());
  }

  private getUpdateNotificationPromises() {
    return this.notifications
      .filter(n => !n.isNew && this.isNotificationChanged(n))
      .map(n => {
        const payload: IUpdateNotificationPayload = {
          ...this.toNotificationPayload(n),
          oldEmail: n.originalSendTo,
        };

        if (n.sendTo !== n.originalSendTo) {
          payload.newEmail = n.sendTo;
        }

        return this._notificationService.update(payload).toPromise();
      });
  }

  private isNotificationChanged(notification: IEditableNotification) {
    const source = this.sourceNotifications.find(n => n.sendTo === notification.originalSendTo);

    return (
      !source ||
      source.sendTo !== notification.sendTo ||
      source.hasAttachmentOnSend !== notification.hasAttachmentOnSend ||
      source.hasAttachmentOnReceive !== notification.hasAttachmentOnReceive ||
      source.onSendStatus !== notification.onSendStatus ||
      source.onReceiveStatus !== notification.onReceiveStatus
    );
  }

  private getDeleteNotificationPromises() {
    return this.deletedNotifications.map(n =>
      this._notificationService.delete({ sendTo: n.originalSendTo }).toPromise()
    );
  }

  private toNotificationPayload(notification: IEditableNotification): INotification {
    const { sendTo, hasAttachmentOnSend, hasAttachmentOnReceive, onSendStatus, onReceiveStatus } = notification;

    return { sendTo, hasAttachmentOnSend, hasAttachmentOnReceive, onSendStatus, onReceiveStatus };
  }

  ngOnInit() {
    this.getNotifications();
  }

  private getNotifications() {
    this._notificationService
      .getAll({ pageNumber: this.notificationsPageNumber, pageSize: this._pageSize })
      .subscribe(notifications => {
        this.sourceNotifications = notifications.records;
        this.resetNotifications();
        this.isLoading = false;
      });
  }
}
